import React, { useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useMediaQuery } from 'react-responsive';
import { useSearchParams } from 'react-router-dom';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Snackbar from '@mui/material/Snackbar';
import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import FlagIcon from '@mui/icons-material/Flag';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import HomeRepairServiceIcon from '@mui/icons-material/HomeRepairService';
import StorefrontIcon from '@mui/icons-material/Storefront';
import SportsMmaIcon from '@mui/icons-material/SportsMma';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import NewspaperIcon from '@mui/icons-material/Newspaper';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import TopBar from '../TopBar';
import agent from '../../agent';
import { BOTTOM_NAVIGATION_SET_POINTS } from '../../constants/actionTypes';
import { DEFAULT_THEME_COLOR } from '../../constants/theme';
import { contextNameKey, evolvedInto, freaksErrorKey, liveEnergy } from '../../utils/theFreaks';
import useFreaksT from './useFreaksT';
import FreaksHero from './FreaksHero';
import FreaksCharacter from './FreaksCharacter';
import FreaksDailyPrize from './FreaksDailyPrize';
import FreaksContextPicker from './FreaksContextPicker';
import FreaksMissions from './FreaksMissions';
import FreaksTraining from './FreaksTraining';
import FreaksGearList from './FreaksGearList';
import FreaksBase from './FreaksBase';
import FreaksFight from './FreaksFight';
import FreaksTopBoard from './FreaksTopBoard';
import FreaksToday from './FreaksToday';
import FreaksFriends from './FreaksFriends';
import FreaksQuests from './FreaksQuests';
import FreaksSafe from './FreaksSafe';
import FreaksWorldBanner from './FreaksWorldBanner';

const TABS = [
  { key: 'missions', Icon: FlagIcon },
  // only with an API that sends quests
  { key: 'quests', Icon: AssignmentTurnedInIcon },
  { key: 'train', Icon: FitnessCenterIcon },
  { key: 'gear', Icon: HomeRepairServiceIcon },
  { key: 'base', Icon: StorefrontIcon },
  { key: 'fight', Icon: SportsMmaIcon },
  { key: 'top', Icon: EmojiEventsIcon },
  { key: 'today', Icon: NewspaperIcon },
  { key: 'friends', Icon: GroupAddIcon },
];
const TAB_KEYS = TABS.map(({ key }) => key);

// The Freaks — Freakland's Crims-style daily game, the same game (same API, same saved progress) as in
// the mobile app: do your activity's missions, train, gear up, build a base, challenge other freaks and
// climb the Top Freaks board. Daily prize + daily champions. Friends tab: invite mutual follows to play.
// Keep in sync with mobile screens/TheFreaks/TheFreaksScreen.tsx.
export const TheFreaksView = (props) => {
  const { t } = useFreaksT();
  const isDesktop = useMediaQuery({ minWidth: 992 });
  const [searchParams, setSearchParams] = useSearchParams();
  const [state, setState] = useState(null);
  const [failed, setFailed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [picker, setPicker] = useState(false);
  const [toast, setToast] = useState(null);
  const [now, setNow] = useState(Date.now());
  const loggedIn = !!props.currentUser?.username;

  const queryTab = searchParams.get('tab');
  const tab = TAB_KEYS.includes(queryTab) ? queryTab : 'missions';
  const setTab = (key) => setSearchParams(key === 'missions' ? {} : { tab: key }, { replace: true });

  const load = useCallback(async () => {
    try {
      setState(await agent.TheFreaks.get());
      setNow(Date.now());
      setFailed(false);
    } catch {
      setFailed(true);
    }
  }, []);

  // once signed in, and again whenever the tab comes back to the foreground (progress may have moved on mobile)
  useEffect(() => {
    if (!loggedIn) return undefined;
    load();
    const onVisible = () => document.visibilityState === 'visible' && load();
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [loggedIn, load]);

  // energy bar and countdowns
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);

  const onRefresh = async () => {
    setRefreshing(true);
    await load();
    setRefreshing(false);
  };

  const notify = (message) => setToast({ ...message, id: Date.now() });

  /** Runs one game action, redraws from the returned state and toasts the outcome. */
  const run = async (call, toastFor) => {
    setBusy(true);
    try {
      const response = await call();
      const evolved = evolvedInto(state, response.state);
      setState(response.state);
      setNow(Date.now());
      const message = toastFor(response.result);
      // reaching a new character stage beats the usual toast; that one moves to the second line
      if (evolved) notify({ type: 'success', text1: t('theFreaks.character.evolved', { name: evolved }), text2: message?.text1 });
      else if (message) notify(message);
      return response.result;
    } catch (error) {
      notify({ type: 'error', text1: t(freaksErrorKey(error)) });
      return null;
    } finally {
      setBusy(false);
    }
  };

  // Freak Points won in the game show up in the top bar right away
  const refreshPoints = (points) => {
    if (points) props.onLoadPoints(Promise.all([agent.Points.count()]));
  };

  const paddingLeft = isDesktop ? (props.leftNavigationOpen ? 300 : 75) : 0;

  const page = (content) => (
    <Box
      sx={{ paddingLeft: `${paddingLeft}px`, minHeight: '100vh', background: 'linear-gradient(180deg, #0d1b2a 0%, #091420 100%)', pb: 12 }}
      data-testid="the-freaks-screen"
    >
      <TopBar/>
      <Box sx={{ px: 2, pt: 2, maxWidth: 720, mx: 'auto', width: '100%', boxSizing: 'border-box' }}>
        {content}
      </Box>
      <Snackbar
        key={toast?.id}
        open={!!toast}
        autoHideDuration={4000}
        onClose={(event, reason) => reason !== 'clickaway' && setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{ bottom: { xs: 80, md: 24 } }}
      >
        {toast ? (
          <Alert severity={toast.type} variant="filled" onClose={() => setToast(null)} sx={{ width: '100%' }}>
            <Box sx={{ fontWeight: 700 }}>{toast.text1}</Box>
            {toast.text2 ? <Box>{toast.text2}</Box> : null}
          </Alert>
        ) : <span/>}
      </Snackbar>
    </Box>
  );

  if (!state) {
    return page(failed ? (
      <Box sx={{ textAlign: 'center', mt: 6, color: 'white' }}>
        <Typography>{t('theFreaks.loadError')}</Typography>
        <Button startIcon={<RefreshIcon/>} onClick={onRefresh} disabled={refreshing} sx={{ mt: 1, minHeight: 44, color: DEFAULT_THEME_COLOR }}>
          {t('theFreaks.fight.refresh')}
        </Button>
      </Box>
    ) : (
      <Box sx={{ textAlign: 'center', mt: 6 }}><CircularProgress sx={{ color: DEFAULT_THEME_COLOR }}/></Box>
    ));
  }

  const energy = liveEnergy(state.energy, state.maxEnergy, state.nextEnergyAt, now).energy;
  const recovering = !!state.recovery && new Date(state.recovery.until).getTime() > now;
  const nameOfMission = (key) => state.missions.find((mission) => mission.key === key)?.name ?? key;
  const hasQuests = Array.isArray(state.quests);

  return page(
    <>
      <FreaksHero
        state={state}
        now={now}
        busy={busy}
        onPickContext={() => setPicker(true)}
        onRefill={() => run(agent.TheFreaks.refill, (r) => ({ type: 'success', text1: t('theFreaks.results.refilled', r) }))}
        onRecover={() => run(agent.TheFreaks.recover, () => ({ type: 'success', text1: t('theFreaks.results.recovered') }))}
      />

      {state.character ? <FreaksCharacter character={state.character} level={state.level}/> : null}

      <FreaksDailyPrize
        daily={state.daily}
        busy={busy}
        onClaim={() => run(agent.TheFreaks.claimDaily, (r) => {
          refreshPoints(r.points);
          return {
            type: 'success',
            text1: t('theFreaks.results.dailyClaimed', r),
            ...(r.points ? { text2: t('theFreaks.results.dailyPoints', r) } : {}),
          };
        })}
      />

      <FreaksWorldBanner world={state.world}/>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <Box role="tablist" sx={{ display: 'flex', gap: 1, overflowX: 'auto', flex: 1, pb: 0.5, scrollbarWidth: 'thin' }}>
          {TABS.filter(({ key }) => key !== 'quests' || hasQuests).map(({ key, Icon }) => (
            <Button
              key={key}
              role="tab"
              aria-selected={tab === key}
              data-testid={`freaks-tab-${key}`}
              startIcon={<Icon/>}
              onClick={() => setTab(key)}
              sx={{
                flexShrink: 0, minHeight: 40, borderRadius: 5, px: 1.75, textTransform: 'none', whiteSpace: 'nowrap',
                fontWeight: tab === key ? 700 : 500,
                color: tab === key ? 'white' : 'rgba(255,255,255,0.8)',
                backgroundColor: tab === key ? DEFAULT_THEME_COLOR : 'rgba(255,255,255,0.08)',
                '&:hover': { backgroundColor: tab === key ? DEFAULT_THEME_COLOR : 'rgba(255,255,255,0.14)' },
              }}
            >
              {t(`theFreaks.tabs.${key}`)}
            </Button>
          ))}
        </Box>
        <Button
          aria-label={t('theFreaks.fight.refresh')}
          onClick={onRefresh}
          disabled={refreshing}
          sx={{ minWidth: 44, minHeight: 40, borderRadius: 5, color: 'rgba(255,255,255,0.8)', flexShrink: 0 }}
          data-testid="freaks-refresh"
        >
          {refreshing ? <CircularProgress size={18} sx={{ color: 'inherit' }}/> : <RefreshIcon/>}
        </Button>
      </Box>

      {tab === 'missions' ? (
        <FreaksMissions
          missions={state.missions}
          energy={energy}
          disabled={busy || recovering}
          maxRepeat={state.maxMissionRepeat}
          onPlay={(mission, times) => run(() => agent.TheFreaks.playMission(mission.key, times), (r) => {
            const name = nameOfMission(mission.key);
            if ((r.runs ?? 1) > 1) {
              return {
                type: r.injuredMinutes ? 'error' : 'success',
                text1: t('theFreaks.results.missionRepeat', { name, ...r }),
                ...(r.injuredMinutes ? { text2: t('theFreaks.results.missionInjured', { name, minutes: r.injuredMinutes }) } : {}),
              };
            }
            if (r.success) return { type: 'success', text1: t('theFreaks.results.missionWon', { name, ...r }) };
            if (r.injuredMinutes) return { type: 'error', text1: t('theFreaks.results.missionInjured', { name, minutes: r.injuredMinutes }) };
            return { type: 'info', text1: t('theFreaks.results.missionFailed', { name }) };
          })}
        />
      ) : null}
      {tab === 'quests' && hasQuests ? (
        <FreaksQuests
          username={state.username}
          quests={state.quests ?? []}
          disabled={busy}
          onClaim={(quest) => run(() => agent.TheFreaks.claimQuest(quest.key), (r) => {
            refreshPoints(r.points);
            return {
              type: 'success',
              text1: t('theFreaks.results.questClaimed', r),
              ...(r.points ? { text2: t('theFreaks.results.dailyPoints', r) } : {}),
            };
          })}
        />
      ) : null}
      {tab === 'train' ? (
        <FreaksTraining
          state={state}
          energy={energy}
          disabled={busy}
          onTrain={(stat) => run(() => agent.TheFreaks.train(stat), (r) => ({
            type: 'success',
            text1: t('theFreaks.results.trained', { stat: t(`theFreaks.stats.${r.stat}`), value: r.value }),
          }))}
        />
      ) : null}
      {tab === 'gear' ? (
        <FreaksGearList
          gear={state.gear}
          cash={state.cash}
          disabled={busy}
          onBuy={(item) => run(() => agent.TheFreaks.buyGear(item.key), () => ({
            type: 'success', text1: t('theFreaks.results.gearBought', { name: item.name }),
          }))}
        />
      ) : null}
      {tab === 'base' ? (
        <FreaksBase
          base={state.base}
          cash={state.cash}
          disabled={busy}
          onCollect={() => run(agent.TheFreaks.collectBase, (r) => ({ type: 'success', text1: t('theFreaks.results.collected', r) }))}
          onUpgrade={() => run(agent.TheFreaks.upgradeBase, (r) => ({
            type: 'success', text1: t('theFreaks.results.baseUpgraded', { name: state.base.name, level: r.level }),
          }))}
          onRepair={state.base.wearing
            ? () => run(agent.TheFreaks.repairBase, (r) => ({ type: 'success', text1: t('theFreaks.results.repaired', r) }))
            : undefined}
        />
      ) : null}
      {tab === 'base' && state.safe ? (
        <FreaksSafe
          safe={state.safe}
          cash={state.cash}
          disabled={busy}
          onDeposit={() => run(() => agent.TheFreaks.deposit('all'), (r) => ({
            type: 'success', text1: t('theFreaks.results.deposited', r),
          }))}
          onWithdraw={() => run(() => agent.TheFreaks.withdraw('all'), (r) => ({
            type: 'success', text1: t('theFreaks.results.withdrawn', r),
          }))}
        />
      ) : null}
      {tab === 'fight' ? (
        <FreaksFight
          state={state}
          energy={energy}
          disabled={busy || recovering}
          onChallenge={async (target) => !!(await run(() => agent.TheFreaks.challenge(target.username, target.isBot), (r) => {
            const params = { ...r, opponent: r.opponentIsBot ? `${r.opponent} 🤖` : `@${r.opponent}` };
            const reduced = r.won && r.lootFactor !== undefined && r.lootFactor < 1
              ? { text2: t('theFreaks.results.lootReduced', { percent: Math.round(r.lootFactor * 100) }) }
              : {};
            return r.won
              ? { type: 'success', text1: t('theFreaks.results.challengeWon', params), ...reduced }
              : { type: 'info', text1: t('theFreaks.results.challengeLost', params) };
          }))}
        />
      ) : null}
      {tab === 'top' ? <FreaksTopBoard myContext={state.context}/> : null}
      {tab === 'today' ? <FreaksToday/> : null}
      {tab === 'friends' ? <FreaksFriends notify={notify}/> : null}

      <Accordion
        data-testid="freaks-rules"
        disableGutters
        sx={{ mt: 2, borderRadius: '12px !important', backgroundColor: 'rgba(255,255,255,0.05)', color: 'white', '&:before': { display: 'none' } }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: 'white' }}/>} sx={{ minHeight: 48 }}>
          <HelpOutlineIcon sx={{ mr: 1.5, color: 'rgba(255,255,255,0.7)' }}/>
          <Typography>{t('theFreaks.rulesTitle')}</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography sx={{ color: 'rgba(255,255,255,0.8)', lineHeight: 1.5, fontSize: 14 }}>{t('theFreaks.rules')}</Typography>
        </AccordionDetails>
      </Accordion>

      <FreaksContextPicker
        open={picker}
        current={state.context}
        contexts={state.contexts}
        onClose={() => setPicker(false)}
        onPick={(context) => {
          setPicker(false);
          if (context === state.context) return;
          run(() => agent.TheFreaks.changeContext(context), () => ({
            type: 'success', text1: t('theFreaks.results.contextChanged', { context: t(contextNameKey(context)) }),
          }));
        }}
      />
    </>,
  );
};

const mapStateToProps = (state) => ({
  currentUser: state.common.currentUser,
  leftNavigationOpen: state.common.leftNavigationOpen,
});

const mapDispatchToProps = (dispatch) => ({
  onLoadPoints: (payload) => dispatch({ type: BOTTOM_NAVIGATION_SET_POINTS, payload }),
});

export default connect(mapStateToProps, mapDispatchToProps)(TheFreaksView);
